<template>
  <VContainer>
    <VRow v-if="isLoading">
      <div class="d-flex flex-column align-center justify-center py-4 w-100">
        <VProgressCircular indeterminate color="primary" />
      </div>
    </VRow>
    <VRow v-else>
      <VCol cols="12">
        <VForm v-model="formValid" @submit.prevent>
          <VRow>
            <!-- Title -->
            <VCol cols="12" sm="6">
              <div class="d-flex align-center h-100 text-title-color font-weight-800">1 - Title:</div>
            </VCol>
            <VCol cols="12" sm="6">
              <VSelect
                variant="outlined"
                color="primary"
                name="title"
                v-model="personalDetails.title"
                :items="titleOptions"
                :rules="formRules.title"
                :disabled="isLoadingForm"
                class="custom-input"
              />
            </VCol>

            <!-- First Name -->
            <VCol cols="12" sm="6">
              <div class="d-flex align-center h-100 text-title-color font-weight-800">2 - First Name:</div>
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                variant="outlined"
                color="primary"
                name="firstName"
                :disabled="isLoadingForm"
                v-model="personalDetails.firstName"
                :rules="formRules.firstName"
                type="text"
                class="custom-input"
              />
            </VCol>

            <!-- Surname -->
            <VCol cols="12" sm="6">
              <div class="d-flex align-center h-100 text-title-color font-weight-800">3 - Surname:</div>
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                variant="outlined"
                color="primary"
                name="lastName"
                :disabled="isLoadingForm"
                v-model="personalDetails.lastName"
                :rules="formRules.lastName"
                type="text"
                class="custom-input"
              />
            </VCol>

            <!-- Full Maiden / Previous Name -->
            <VCol cols="12" sm="6">
              <div class="d-flex align-center h-100 text-title-color font-weight-800">
                4 - Full Maiden / Previous Name
              </div>
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                variant="outlined"
                color="primary"
                name="fullMaidenName"
                v-model="personalDetails.fullMaidenName"
                :disabled="isLoadingForm"
                :rules="formRules.fullMaidenName"
                type="text"
                class="custom-input"
              />
            </VCol>

            <!-- Date of Birth -->
            <VCol cols="12" sm="6">
              <div class="d-flex align-center h-100 text-title-color font-weight-800">5 - Date of Birth:</div>
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                variant="outlined"
                color="primary"
                name="dob"
                v-model="personalDetails.dob"
                :disabled="isLoadingForm"
                :rules="formRules.dob"
                type="date"
                :max="maxDate"
                class="custom-input"
              />
            </VCol>

            <!-- National Insurance Number -->
            <VCol cols="12" sm="6">
              <div class="d-flex align-center h-100 text-title-color font-weight-800">
                6 - What is your National Insurance Number?
              </div>
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                variant="outlined"
                color="primary"
                name="nationalInsuranceNumber"
                v-model="personalDetails.nationalInsuranceNumber"
                :disabled="isLoadingForm"
                :rules="formRules.nationalInsuranceNumber"
                type="text"
                placeholder="e.g. AB123456C"
                @input="formatNino"
                class="custom-input"
              />
            </VCol>

            <!-- Private UK Pension - Yes/No Radio -->
            <VCol cols="12" sm="6">
              <div class="d-flex align-center h-100 text-title-color font-weight-800">
                7 - Have You a Private UK Pension?
              </div>
            </VCol>
            <VCol cols="12" sm="6">
              <VRadioGroup v-model="personalDetails.isPrivateUKPension" :rules="formRules.isPrivateUKPension" inline>
                <VRadio label="Yes" :value="true" color="primary"></VRadio>
                <VRadio label="No" :value="false" color="primary"></VRadio>
              </VRadioGroup>
            </VCol>

            <!-- Current Occupation -->
            <VCol cols="12" sm="6">
              <div class="d-flex align-center h-100 text-title-color font-weight-800">8 - Your Current Occupation:</div>
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                variant="outlined"
                color="primary"
                name="currentOccupation"
                v-model="personalDetails.currentOccupation"
                :disabled="isLoadingForm"
                :rules="formRules.currentOccupation"
                type="text"
                class="custom-input"
              />
            </VCol>

            <VCol cols="12" sm="6">
              <div class="d-flex h-100 text-title-color font-weight-800">9 - Your Current Address Abroad:</div>
            </VCol>

            <VCol cols="12" sm="6">
              <VRow>
                <VCol cols="12">
                  <div class="d-flex align-center mb-2 text-title-color font-weight-800">Number & Street:</div>
                  <VTextField
                    variant="outlined"
                    color="primary"
                    name="street"
                    v-model="personalDetails.currentAddress.street"
                    :disabled="isLoadingForm"
                    :rules="formRules.street"
                    type="text"
                    placeholder="e.g. 18 Silverwood Heights"
                    class="custom-input"
                  />
                </VCol>

                <VCol cols="12" sm="6">
                  <div class="d-flex align-center mb-2 text-title-color font-weight-800">Town/District:</div>
                  <VTextField
                    variant="outlined"
                    color="primary"
                    name="city"
                    v-model="personalDetails.currentAddress.city"
                    :disabled="isLoadingForm"
                    :rules="formRules.city"
                    type="text"
                    placeholder="e.g. Cork"
                    class="custom-input"
                  />
                </VCol>

                <VCol cols="12" sm="6">
                  <div class="d-flex align-center mb-2 text-title-color font-weight-800">County/State:</div>
                  <VSelect
                    variant="outlined"
                    color="primary"
                    name="state"
                    v-model="personalDetails.currentAddress.state"
                    :items="stateOptions"
                    :disabled="isLoadingForm || !personalDetails.currentAddress.country"
                    :rules="formRules.state"
                    class="custom-input"
                    :placeholder="!personalDetails.currentAddress.country ? 'Select country first' : 'Select state'"
                  />
                </VCol>

                <VCol cols="12" sm="6">
                  <div class="d-flex align-center mb-2 text-title-color font-weight-800">Postcode:</div>
                  <VTextField
                    variant="outlined"
                    color="primary"
                    name="postalCode"
                    v-model="personalDetails.currentAddress.postalCode"
                    :disabled="isLoadingForm"
                    :rules="formRules.postalCode"
                    type="text"
                    placeholder="e.g. P32 TX27"
                    class="custom-input"
                  />
                </VCol>

                <VCol cols="12" sm="6">
                  <div class="d-flex align-center mb-2 text-title-color font-weight-800">Country:</div>
                  <VSelect
                    variant="outlined"
                    color="primary"
                    name="country"
                    v-model="personalDetails.currentAddress.country"
                    :items="countryOptions"
                    :disabled="isLoadingForm"
                    :rules="formRules.country"
                    class="custom-input"
                    @update:model-value="onCountryChange"
                  />
                </VCol>
              </VRow>
            </VCol>

            <!-- Email -->
            <VCol cols="12" sm="6">
              <div class="d-flex align-center h-100 text-title-color font-weight-800">10 - Email</div>
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                variant="outlined"
                color="primary"
                name="email"
                v-model="personalDetails.email"
                :disabled="isLoadingForm"
                :rules="formRules.email"
                type="email"
                placeholder="e.g. name@example.com"
                class="custom-input"
                readonly
              />
            </VCol>
            <!-- Phome -->
            <VCol cols="12" sm="6">
              <div class="d-flex align-center h-100 text-title-color font-weight-800">11 - Phone Number</div>
            </VCol>
            <VCol cols="12" sm="6">
              <VTextField
                variant="outlined"
                color="primary"
                name="phone"
                v-model="personalDetails.phone"
                :disabled="isLoadingForm"
                :rules="formRules.phone"
                type="phone"
                placeholder="Enter"
                class="custom-input"
                readonly
              />
            </VCol>
            <!-- Submit Button -->
            <VCol cols="12" class="text-center mt-4">
              <VBtn :loading="isLoadingForm" type="submit" block flat @click="handleUpdate()" color="primary">
                Update Personal Information
              </VBtn>
            </VCol>
          </VRow>
        </VForm>
      </VCol>
    </VRow>
  </VContainer>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
// Service
import applicationService from '@/services/application-service/application.service';
export default {
  name: 'PersonalDetailsPage',
  inject: ['countryListOptions'],
  computed: {
    ...mapGetters('user', ['userId', 'application']),
    maxDate() {
      const today = new Date();
      const minAgeDate = new Date(today.setFullYear(today.getFullYear() - 16));
      return minAgeDate.toISOString().split('T')[0];
    },
    countryOptions() {
      const options = [{ title: 'Select Country', value: null }];

      for (const countryCode in this.countryListOptions) {
        const country = this.countryListOptions[countryCode];
        options.push({
          title: country.name,
          value: countryCode.toUpperCase()
        });
      }

      return options;
    },
    stateOptions() {
      const options = [{ title: 'Select County/State', value: null }];

      const countryCode = this.personalDetails.currentAddress.country?.toLowerCase();
      if (countryCode && this.countryListOptions[countryCode]?.states) {
        const states = this.countryListOptions[countryCode].states;
        for (const stateCode in states) {
          options.push({
            title: states[stateCode],
            value: states[stateCode]
          });
        }
      }

      return options;
    },
    hasStateOptions() {
      const countryCode = this.personalDetails.currentAddress.country?.toLowerCase();
      return (
        countryCode &&
        this.countryListOptions[countryCode]?.states &&
        Object.keys(this.countryListOptions[countryCode].states).length > 0
      );
    }
  },
  data() {
    return {
      isLoading: true,
      isLoadingForm: false,
      formValid: false,
      formRules: {
        title: [(v) => !!v || 'Title is required'],

        firstName: [(v) => !!v || 'First name is required'],

        lastName: [(v) => !!v || 'Surname is required'],

        fullMaidenName: [],

        dob: [
          (v) => !!v || 'Date of birth is required',
          (v) => {
            if (!v) return true;
            const selectedDate = new Date(v);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return selectedDate < today || 'Date of birth must be in the past';
          },
          (v) => {
            if (!v) return true;
            const selectedDate = new Date(v);
            const today = new Date();
            const minAgeDate = new Date(today.setFullYear(today.getFullYear() - 16));
            return selectedDate <= minAgeDate || 'You must be at least 16 years old';
          }
        ],

        nationalInsuranceNumber: [
          (v) => !!v || 'National Insurance Number is required',
          (v) => {
            if (!v) return true;
            const ninoRegex = /^(?!BG|GB|NK|KN|TN|NT|ZZ)[A-CEGHJ-PR-TW-Z]{2}[0-9]{6}[A-D]{1}$/;
            const cleanNino = v.replace(/\s/g, '').toUpperCase();
            return ninoRegex.test(cleanNino) || 'Please enter a valid UK National Insurance Number (e.g., AB123456C)';
          }
        ],

        isPrivateUKPension: [(v) => v !== null || 'Please select an option'],

        currentOccupation: [(v) => !!v || 'Current occupation is required'],

        street: [(v) => !!v || 'Street address is required'],

        city: [(v) => !!v || 'Town/District is required'],

        state: [
          (v) => {
            if (this.hasStateOptions) {
              return !!v || 'County/State is required for this country';
            }
            return true;
          }
        ],

        postalCode: [(v) => !!v || 'Postcode is required'],

        country: [(v) => !!v || 'Country is required'],
        email: [(v) => !!v || 'Email required', (v) => /.+@.+\..+/.test(v) || 'E-mail must be valid'],
        phone: [(v) => !!v || 'Phone is required']
      },
      titleOptions: [
        { title: 'Choose', value: null },
        { title: 'Mr', value: 'Mr' },
        { title: 'Mrs', value: 'Mrs' },
        { title: 'Ms', value: 'Ms' },
        { title: 'Dr', value: 'Dr' },
        { title: 'Prof', value: 'Prof' },
        { title: 'Fr', value: 'Fr' },
        { title: 'Sr', value: 'Sr' }
      ],
      personalDetails: {
        title: '',
        firstName: '',
        lastName: '',
        fullMaidenName: '',
        dob: '',
        nationalInsuranceNumber: '',
        isPrivateUKPension: '',
        currentOccupation: '',
        currentAddress: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: '',
          countryCode: ''
        },
        email: '',
        phone: ''
      }
    };
  },
  mounted() {
    this.getApplicationDetails();
  },
  methods: {
    ...mapActions('main', ['showSnackbar']),
    ...mapActions('user', ['updateUserApplication']),

    formatNino(event) {
      let value = event.target.value.replace(/\s/g, '').toUpperCase();
      if (value.length > 0) {
        const parts = [];
        if (value.length > 2) {
          parts.push(value.substring(0, 2));
          if (value.length > 4) {
            parts.push(value.substring(2, 4));
            if (value.length > 6) {
              parts.push(value.substring(4, 6));
              if (value.length > 8) {
                parts.push(value.substring(6, 8));
                parts.push(value.substring(8, 9));
              } else {
                parts.push(value.substring(6));
              }
            } else {
              parts.push(value.substring(4));
            }
          } else {
            parts.push(value.substring(2));
          }
        } else {
          parts.push(value);
        }
        this.personalDetails.nationalInsuranceNumber = parts.join(' ');
      }
    },

    onCountryChange(newCountry) {
      this.personalDetails.currentAddress.state = null;
      if (newCountry) {
        const countryCode = newCountry.toLowerCase();
        if (this.countryListOptions[countryCode]) {
          this.personalDetails.currentAddress.countryCode = this.countryListOptions[countryCode].key3 || '';
        }
      } else {
        this.personalDetails.currentAddress.countryCode = '';
      }
    },

    async getApplicationDetails() {
      this.isLoading = true;
      try {
        const { application } = await applicationService.getApplicationDetails();

        let countryCode = application.currentAddress?.countryCode || '';
        let country = application.currentAddress?.country || '';

        if (country && !countryCode) {
          for (const code in this.countryListOptions) {
            if (this.countryListOptions[code].name.toLowerCase() === country.toLowerCase()) {
              countryCode = code.toUpperCase();
              break;
            }
          }
        }

        this.personalDetails = {
          title: application.title || '',
          firstName: application.firstName || '',
          lastName: application.lastName || '',
          fullMaidenName: application.fullMaidenName || '',
          dob: application.dob || '',
          nationalInsuranceNumber: application.nationalInsuranceNumber || '',
          isPrivateUKPension: application.isPrivateUKPension ? true : false,
          currentOccupation: application.currentOccupation || '',
          currentAddress: {
            street: application.currentAddress?.street || '',
            city: application.currentAddress?.city || '',
            state: application.currentAddress?.state || '',
            postalCode: application.currentAddress?.postalCode || '',
            country: countryCode || '',
            countryCode: countryCode || ''
          },
          email: application.email || '',
          phone: application.phone || ''
        };
      } catch (err) {
        console.error(err);
        this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
      } finally {
        this.isLoading = false;
      }
    },

    async handleUpdate() {
      if (this.formValid === true) {
        const application = {
          ...this.personalDetails,
          currentAddress: {
            ...this.personalDetails.currentAddress,
            country: this.countryListOptions[this.personalDetails.currentAddress.country?.toLowerCase()]?.name || '',
            countryCode: this.personalDetails.currentAddress.countryCode || this.personalDetails.currentAddress.country
          }
        };
        this.isLoadingForm = true;
        try {
          const res = await applicationService.updateApplicationDetails({ application: application });
          if (res?.application?.id) {
            this.updateUserApplication(application);
            this.showSnackbar({
              text: 'Personal details saved successfully',
              color: 'success'
            });
          }
        } catch (err) {
          console.error(err);
          this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
        } finally {
          this.isLoadingForm = false;
        }
      }
    }
  }
};
</script>
